import React, { useState } from 'react';
import { useOrganization, Organization } from '../../contexts/OrganizationContext';
import { useTerminology } from '../../hooks/useTerminology';
import { isDomRole } from '../../utils/roleHelpers';

interface MobileOrgSwitcherProps {
  onSwitch?: () => void;
}

/**
 * Inline org switcher for mobile slide menus.
 * - Multi-org: tap-to-expand list for switching.
 * - Single-org: shows org name as a static label only.
 * Uses OrganizationContext directly — no props needed for data.
 */
const MobileOrgSwitcher: React.FC<MobileOrgSwitcherProps> = ({ onSwitch }) => {
  const { organization, organizations, selectOrganization } = useOrganization();
  const { t } = useTerminology();
  const [expanded, setExpanded] = useState(false);

  if (!organization) return null;

  const isMultiOrg = organizations.length > 1;

  const handleSelect = (org: Organization) => {
    selectOrganization(org);
    setExpanded(false);
    onSwitch?.();
  };

  // Single-org: show org name as a static label — no actions here
  if (!isMultiOrg) {
    return (
      <div className="mb-2 py-3 px-4 bg-[var(--theme-bg-tertiary)] rounded-lg">
        <span className="block text-[10px] font-semibold uppercase tracking-wider text-text-tertiary mb-0.5">Hierarchy</span>
        <span className="block text-[15px] font-bold text-[var(--theme-primary)]">{organization.name}</span>
      </div>
    );
  }

  // Multi-org: tap-to-expand switcher
  return (
    <div className="mb-2">
      <button
        className="flex items-center justify-between w-full py-3 px-4 border-none bg-[var(--theme-bg-tertiary)] rounded-lg cursor-pointer text-left"
        onClick={() => setExpanded(!expanded)}
        type="button"
        aria-expanded={expanded}
      >
        <span>
          <span className="block text-[10px] font-semibold uppercase tracking-wider text-text-tertiary mb-0.5">Hierarchy</span>
          <span className="block text-[15px] font-bold text-[var(--theme-primary)]">{organization.name}</span>
        </span>
        <svg
          className={`shrink-0 ml-2 text-text-secondary transition-transform duration-200 ${expanded ? 'rotate-180' : 'rotate-0'}`}
          width="20" height="20" viewBox="0 0 20 20" fill="none"
          aria-hidden="true"
        >
          <path d="M7 8l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {expanded && (
        <div className="flex flex-col gap-0.5 py-1">
          {organizations.map((org) => (
            <button
              key={org.id}
              className={`flex items-center gap-2 w-full py-2.5 px-4 border-none bg-none cursor-pointer text-left rounded-sm transition-colors hover:bg-[var(--theme-hover)] ${
                org.id === organization.id ? 'bg-[var(--theme-bg-tertiary)]' : ''
              }`}
              onClick={() => handleSelect(org)}
              type="button"
            >
              <span className="text-sm font-medium flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{org.name}</span>
              <span className="text-[11px] text-text-secondary shrink-0">
                {isDomRole(org.role) ? t('dominant') : t('submissive')}
              </span>
              {org.id === organization.id && (
                <span className="text-[10px] font-semibold uppercase text-success shrink-0">Active</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MobileOrgSwitcher;
